import { Task, WeeklyPlan, PhysicalUnit, Responsible, ProjectProgressSnapshot, ProtocolState, ProjectInfo, GanttBaseline } from '../types';

export interface SCurvePoint {
    name: string;
    date: string;
    weekIndex: number;
    plan: number;
    real: number | null;
}

/**
 * Calculates the S-Curve data (Planned vs Real progress over time)
 * @param tasks - List of project tasks
 * @returns Array of SCurvePoint for charting
 */
export const calculateSCurve = (tasks: Task[], progressHistory: ProjectProgressSnapshot[] = []): SCurvePoint[] => {
    const flatTasks: Task[] = [];
    const traverse = (t: Task[]) => t.forEach(task => {
        const hasSubtasks = task.children && task.children.some(c => c.isWeeklySubtask);
        if (task.type === 'task' && task.start) {
            flatTasks.push(task);
        }
        if (task.children && !hasSubtasks) {
            traverse(task.children);
        }
    });
    traverse(tasks);

    if (flatTasks.length === 0) return [];

    // Find global range
    const starts = flatTasks.map(t => new Date(t.start! + "T12:00:00").getTime());
    const ends = flatTasks.map(t => new Date((t.end || t.start)! + "T12:00:00").getTime());
    const minStart = new Date(Math.min(...starts));
    const maxEnd = new Date(Math.max(...ends));

    // Generate weekly points
    const points: SCurvePoint[] = [];
    let curr = new Date(minStart);

    // Normalize to start of week (Monday)
    const day = curr.getDay();
    const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
    curr.setDate(diff);

    const totalProjectDuration = flatTasks.reduce((sum, t) => sum + (t.duration || 0), 0);
    const now = new Date();

    while (curr <= maxEnd || points.length < 4) {
        const dateStr = curr.toISOString().split('T')[0];
        const dateLabel = curr.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
        const compareTime = curr.getTime();

        // 1. Calculate Planned Progress
        let plannedDurationAccum = 0;
        flatTasks.forEach(t => {
            const tStart = new Date(t.start! + "T12:00:00").getTime();
            const tEnd = new Date((t.end || t.start)! + "T12:00:00").getTime();
            const tDur = t.duration || 0;

            if (compareTime >= tEnd) {
                plannedDurationAccum += tDur;
            } else if (compareTime > tStart) {
                const totalDays = Math.max(86400000, tEnd - tStart);
                const elapsedDays = compareTime - tStart;
                plannedDurationAccum += (elapsedDays / totalDays) * tDur;
            }
        });

        const planPct = totalProjectDuration > 0
            ? Number(truncateProgress((plannedDurationAccum / totalProjectDuration) * 100, 2))
            : 0;

        // 2. Calculate Real Progress (from history)
        let realPct: number | null = null;

        if (progressHistory && Array.isArray(progressHistory) && progressHistory.length > 0) {
            // Sort to be sure
            const validHistory = progressHistory.filter(h => h && h.date);
            if (validHistory.length > 0) {
                const sortedHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));
                const lastRecord = sortedHistory[sortedHistory.length - 1];
                const lastRecordTime = new Date(lastRecord.date + "T23:59:59").getTime();

            // Only show real data up to and including the week of the last recorded sync
            if (compareTime <= lastRecordTime) {
                // Filter records up to the END of the current chart week
                const endOfCurrentWeekStr = new Date(compareTime + (7 * 86400000)).toISOString().split('T')[0];
                const pastOrCurrentRecords = sortedHistory.filter(r => r.date <= endOfCurrentWeekStr);

                if (pastOrCurrentRecords.length > 0) {
                    realPct = pastOrCurrentRecords[pastOrCurrentRecords.length - 1].progress;
                } else {
                    // Start from 0% at the beginning of the project
                    realPct = 0;
                }
            }
            }
        }

        points.push({
            name: dateLabel,
            date: dateStr, // Added for precise matching
            weekIndex: points.length + 1, // Added sequential week number
            plan: Math.min(100, planPct),
            real: realPct !== null ? Math.min(100, realPct) : null
        });

        curr.setDate(curr.getDate() + 7);
        if (points.length > 100) break; // Hard limit for safety
    }
    return points;
};

/**
 * Truncates a number to a specific number of decimal places without rounding.
 */
export const truncateProgress = (val: number, decimals: number = 2): string => {
    if (isNaN(val) || val === null) return (0).toFixed(decimals);
    const factor = Math.pow(10, decimals);
    // Use a small epsilon to avoid floating point errors like 1.000000000000000001
    return (Math.floor(val * factor + 0.0000001) / factor).toFixed(decimals);
};

export const isProtocolProgressed = (
    taskId: string,
    unitId: string,
    savedProtocols: Record<string, ProtocolState>,
    protocolConfigs: any
): boolean => {
    return Object.values(savedProtocols).some((p: ProtocolState) => {
        if (p.taskId === taskId && p.unitId === unitId) {
            const config = protocolConfigs[taskId];
            const group = p.groupId
                ? config?.reviewerGroups?.find((g: any) => g.id === p.groupId)
                : config?.reviewerGroups?.find((g: any) => g.unitIds.includes(p.unitId));

            if (group) {
                return group.reviewers.some((rev: any, idx: number) =>
                    rev.generatesProgress && p.signatures[idx]
                );
            }
        }
        return false;
    });
};

export const calculateUnitProgress = (
    taskId: string,
    unitId: string,
    protocolConfigs: any,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[],
    tasks: Task[] = []
): number => {
    const taskObj = findTaskById(taskId, tasks);
    const parentTask = findParentTask(taskId, tasks);
    const isSub = taskObj?.isWeeklySubtask ?? false;
    const parentTaskId = (isSub && parentTask) ? parentTask.id : taskId;
    const subtaskId = taskId;

    const config = protocolConfigs[parentTaskId];
    const appliesProtocol = config?.applies ?? false;

    if (appliesProtocol && isProtocolProgressed(parentTaskId, unitId, savedProtocols, protocolConfigs)) {
        return 100;
    }

    // Sum up % Cumplimiento in all closed weekly plans
    let totalCumplimiento = 0;
    weeklyPlans.forEach(plan => {
        if (plan.status === 'Cerrada') {
            plan.allocations.forEach(alloc => {
                if (alloc.taskId === subtaskId && alloc.unitId === unitId) {
                    if (alloc.percentCumplimiento !== undefined) {
                        totalCumplimiento += alloc.percentCumplimiento;
                    } else if (alloc.executed) {
                        totalCumplimiento += 100;
                    }
                }
            });
        }
    });

    return Math.min(totalCumplimiento, 100);
};

export const calculateTaskProgress = (
    taskId: string,
    units: PhysicalUnit[],
    matrixData: Record<string, any>,
    protocolConfigs: any,
    projectInfo: ProjectInfo,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[],
    tasks: Task[] = []
): number => {
    const taskObj = findTaskById(taskId, tasks);
    const parentTask = findParentTask(taskId, tasks);
    const targetTaskId = (taskObj?.isWeeklySubtask && parentTask) ? parentTask.id : taskId;

    // 1. Get Total Assigned Units from EDT (Matrix)
    const assignedUnits = units.filter(u => {
        const val = matrixData[`planning_${targetTaskId}_${u.id}`];
        return val !== undefined && val !== null && val !== '' && val !== '0' && val !== '-';
    });

    if (assignedUnits.length === 0) return 0;

    // 2. Sum progress of all assigned units using the cubicación weights
    let totalCubicacion = 0;
    let totalEjecutado = 0;

    assignedUnits.forEach(u => {
        const val = matrixData[`planning_${targetTaskId}_${u.id}`];
        const cubicacion = (val && !isNaN(parseFloat(val))) ? parseFloat(val) : 1;
        
        // Pass tasks here so calculateUnitProgress can find targetTaskId and taskId correctly
        const progressPercent = calculateUnitProgress(taskId, u.id, protocolConfigs, savedProtocols, weeklyPlans, tasks);
        
        totalCubicacion += cubicacion;
        totalEjecutado += cubicacion * (progressPercent / 100);
    });

    if (totalCubicacion === 0) return 0;
    return (totalEjecutado / totalCubicacion) * 100;
};

/**
 * Calculates the total project progress based on individual task completion.
 */
export const calculateProjectProgress = (
    tasks: Task[],
    units: PhysicalUnit[],
    matrixData: Record<string, any>,
    protocolConfigs: any,
    projectInfo: ProjectInfo,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[]
): number => {
    let totalWeightedProgress = 0;
    let totalDuration = 0;

    const traverse = (list: Task[]) => {
        list.forEach(t => {
            const hasSubtasks = t.children && t.children.some(c => c.isWeeklySubtask);
            if (t.type === 'task' && (!t.children || t.children.length === 0 || hasSubtasks)) {
                let progress = 0;
                if (hasSubtasks) {
                    const weeklySubtasks = t.children!.filter(c => c.isWeeklySubtask);
                    const subtaskProgresses = weeklySubtasks.map(child => {
                        let physicalProgress = calculateTaskProgress(child.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                        const subtaskProgress = Math.min(100, (child.baseProgress !== undefined ? child.baseProgress : (child.progress || 0)) + physicalProgress);
                        const weight = child.weight !== undefined ? child.weight : (100 / weeklySubtasks.length);
                        return subtaskProgress * (weight / 100);
                    });
                    progress = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
                } else {
                    let physicalProgress = calculateTaskProgress(t.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                    progress = Math.min(100, (t.baseProgress !== undefined ? t.baseProgress : (t.progress || 0)) + physicalProgress);
                }

                const duration = t.duration || 0;
                totalWeightedProgress += (progress * duration);
                totalDuration += duration;
            } else if (t.children) {
                traverse(t.children);
            }
        });
    };

    traverse(tasks);
    return totalDuration > 0 ? (totalWeightedProgress / totalDuration) : 0;
};

/**
 * Calculates a duration-weighted global progress percentage based on task.progress property.
 * Useful for historical snapshots or when "Live" calculation is not needed.
 */
export const calculateWeightedProgress = (tasks: Task[]): number => {
    let totalWeightedProgress = 0;
    let totalDuration = 0;

    const traverse = (list: Task[]) => {
        list.forEach(t => {
            const hasSubtasks = t.children && t.children.some(c => c.isWeeklySubtask);
            if (t.type === 'task' && (!t.children || t.children.length === 0 || hasSubtasks)) {
                let progress = 0;
                if (hasSubtasks) {
                    const subtaskProgresses = t.children!.map(child => child.progress || 0);
                    progress = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
                } else {
                    progress = t.progress || 0;
                }
                const duration = t.duration || 0;
                totalWeightedProgress += (progress * duration);
                totalDuration += duration;
            } else if (t.children) {
                traverse(t.children);
            }
        });
    };

    traverse(tasks);
    return totalDuration > 0 ? (totalWeightedProgress / totalDuration) : 0;
};

/**
 * Calculates how many days behind the real progress is compared to the planned curve.
 */
export const calculateDelayDays = (curveData: any[], currentRealPct: number, currentDate: string): number => {
    if (curveData.length === 0) return 0;

    const getPlanValue = (point: any) => {
        if (!point) return 0;
        return point.planActive !== undefined && point.planActive !== null ? point.planActive : (point.plan || 0);
    };

    // Si el avance real es 0 (o menor), comparamos la fecha actual contra el inicio programado del proyecto.
    // Si la fecha actual ya superó el inicio y seguimos en 0%, hay un retraso real igual a los días transcurridos.
    if (currentRealPct <= 0) {
        const sortedCurve = [...curveData].sort((a, b) => a.date.localeCompare(b.date));
        const projectStartTime = new Date(sortedCurve[0].date + "T12:00:00").getTime();
        const actualTime = new Date(currentDate + "T12:00:00").getTime();
        if (actualTime > projectStartTime) {
            const diffTime = actualTime - projectStartTime;
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
        return 0;
    }

    // Ensure sorted by date
    const sortedCurve = [...curveData].sort((a, b) => a.date.localeCompare(b.date));

    // Find the first week where the planned progress meets or exceeds our current real progress
    const targetIdx = sortedCurve.findIndex(p => getPlanValue(p) >= currentRealPct);
    
    if (targetIdx === -1) {
        // Real progress is ahead of all planned points
        return 0;
    }

    let plannedTime: number;

    if (targetIdx === 0) {
        // It's before or exactly at the first week
        plannedTime = new Date(sortedCurve[0].date + "T12:00:00").getTime();
    } else {
        const pPrev = sortedCurve[targetIdx - 1];
        const pCurr = sortedCurve[targetIdx];
        
        const planPrev = getPlanValue(pPrev);
        const planCurr = getPlanValue(pCurr);
        
        const timePrev = new Date(pPrev.date + "T12:00:00").getTime();
        const timeCurr = new Date(pCurr.date + "T12:00:00").getTime();
        
        const planDiff = planCurr - planPrev;
        
        if (planDiff > 0) {
            const ratio = (currentRealPct - planPrev) / planDiff;
            plannedTime = timePrev + ratio * (timeCurr - timePrev);
        } else {
            plannedTime = timeCurr;
        }
    }

    const actualTime = new Date(currentDate + "T12:00:00").getTime();
    const diffTime = actualTime - plannedTime;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays); // Return positive delay
};

/**
 * Flattens a task tree into a flat array of tasks.
 */
export const flattenTasks = (list: Task[]): Task[] => {
    if (!list) return [];
    let flat: Task[] = [];
    list.forEach(t => {
        if (t.isDeleted) return;
        flat.push(t);
        if (t.children) {
            flat = flat.concat(flattenTasks(t.children.filter(c => !c.isDeleted)));
        }
    });
    return flat;
};

/**
 * Finds a task by ID within a tree structure.
 */
export const findTaskById = (id: string | null, list: Task[]): Task | null => {
    if (!id || !list) return null;
    for (const t of list) {
        if (t.id === id) return t;
        if (t.children) {
            const found = findTaskById(id, t.children);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Finds the name of a task by ID within a tree structure.
 */
export const findTaskName = (id: string, list: Task[]): string => {
    if (!id || !list) return "";
    for (const t of list) {
        if (t.id === id) return t.name;
        if (t.children) {
            const found = findTaskName(id, t.children);
            if (found) return found;
        }
    }
    return "";
};

/**
 * Calculates the Critical Path Method (CPM) for a given set of tasks.
 * Marks `isCritical: true` on tasks that belong to the critical path (slack <= 0).
 */
/**
 * Calculates the Critical Path Method (CPM) for a given set of tasks.
 * Marks `isCritical: true` on tasks that belong to the critical path (slack <= 0).
 */
export const calculateCriticalPath = (
    tasks: Task[], 
    workingDays: boolean[] = [false, true, true, true, true, true, false], 
    holidays: { date: string; name: string }[] = []
): Task[] => {
    if (tasks.length === 0) return tasks;

    // Helper to get local date string
    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    // Helper to check if a day is a working day
    const isWorkingDay = (date: Date): boolean => {
        const dayOfWeek = date.getDay();
        if (!workingDays[dayOfWeek]) return false;
        const dateStr = getLocalDateStr(date);
        return !holidays.some(h => h.date === dateStr);
    };

    // Helper to calculate duration in working days
    const calculateWorkingDaysDuration = (start: string, end: string): number => {
        const startD = new Date(start + "T12:00:00");
        const endD = new Date(end + "T12:00:00");
        if (isNaN(startD.getTime()) || isNaN(endD.getTime())) return 0;
        if (startD > endD) return 0;

        let count = 0;
        let curr = new Date(startD);
        while (curr <= endD) {
            if (isWorkingDay(curr)) {
                count++;
            }
            curr.setDate(curr.getDate() + 1);
        }
        return count;
    };

    // Recursive function to compute folder dates
    const computeFolderDatesRecursive = (list: Task[]): Task[] => {
        return list.map(t => {
            if (t.type === 'folder') {
                const children = t.children ? computeFolderDatesRecursive(t.children) : [];
                
                let minStart: Date | null = null;
                let maxEnd: Date | null = null;

                // Filtrar para excluir subtareas semanales del cálculo de fechas de la carpeta
                const masterChildren = children.filter(c => !c.isWeeklySubtask);

                masterChildren.forEach(c => {
                    if (c.start) {
                        const d = new Date(c.start + "T12:00:00");
                        if (!isNaN(d.getTime())) {
                            if (!minStart || d < minStart) minStart = d;
                        }
                    }
                    if (c.end) {
                        const d = new Date(c.end + "T12:00:00");
                        if (!isNaN(d.getTime())) {
                            if (!maxEnd || d > maxEnd) maxEnd = d;
                        }
                    }
                });

                let start = t.start;
                let end = t.end;
                let duration = t.duration;

                if (minStart && maxEnd) {
                    start = getLocalDateStr(minStart);
                    end = getLocalDateStr(maxEnd);
                    duration = calculateWorkingDaysDuration(start, end);
                } else {
                    start = undefined;
                    end = undefined;
                    duration = 0;
                }

                return {
                    ...t,
                    children, // Se mantienen todos los hijos en el árbol
                    start,
                    end,
                    duration
                };
            } else {
                return t;
            }
        });
    };

    const tasksWithCalculatedFolders = computeFolderDatesRecursive(tasks);

    // 1. Flatten all tasks
    const flatTasks = flattenTasks(tasksWithCalculatedFolders);
    if (flatTasks.length === 0) return tasksWithCalculatedFolders;

    // Filter to only consider leaf tasks (tasks without children, ignoring weekly planning subtasks)
    const leafTasks = flatTasks.filter(t => 
        !t.isWeeklySubtask && 
        t.type === 'task' && 
        (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))
    );
    if (leafTasks.length === 0) return tasksWithCalculatedFolders;

    // Find min and max dates to generate the working calendar range
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    leafTasks.forEach(t => {
        if (t.start) {
            const d = new Date(t.start + "T12:00:00");
            if (!minDate || d < minDate) minDate = d;
        }
        const tEnd = t.end || t.start;
        if (tEnd) {
            const d = new Date(tEnd + "T12:00:00");
            if (!maxDate || d > maxDate) maxDate = d;
        }
    });

    if (!minDate || !maxDate) return tasksWithCalculatedFolders;

    // Generate a range of calendar days with safety buffer (+/- 60 days)
    const startRange = new Date(minDate);
    startRange.setDate(startRange.getDate() - 60);
    const endRange = new Date(maxDate);
    endRange.setDate(endRange.getDate() + 60);

    const workingDaysList: string[] = [];
    const dateToIdx = new Map<string, number>();
    const idxToDate = new Map<number, string>();

    let curr = new Date(startRange);
    let idx = 0;
    while (curr <= endRange) {
        if (isWorkingDay(curr)) {
            const dateStr = getLocalDateStr(curr);
            workingDaysList.push(dateStr);
            dateToIdx.set(dateStr, idx);
            idxToDate.set(idx, dateStr);
            idx++;
        }
        curr.setDate(curr.getDate() + 1);
    }

    // Helper to find the closest working day index for a given date
    const getWorkingDayIdx = (dateStr: string): number => {
        if (dateToIdx.has(dateStr)) return dateToIdx.get(dateStr)!;
        // Fallback: search for the closest indexed working day
        const targetDate = new Date(dateStr + "T12:00:00");
        let bestIdx = 0;
        let minDiff = Number.MAX_SAFE_INTEGER;
        
        workingDaysList.forEach((wDateStr, wIdx) => {
            const wDate = new Date(wDateStr + "T12:00:00");
            const diff = Math.abs(wDate.getTime() - targetDate.getTime());
            if (diff < minDiff) {
                minDiff = diff;
                bestIdx = wIdx;
            }
        });
        return bestIdx;
    };

    // Data structures for CPM
    const es = new Map<string, number>(); // Early Start (index)
    const ef = new Map<string, number>(); // Early Finish (index)
    const ls = new Map<string, number>(); // Late Start (index)
    const lf = new Map<string, number>(); // Late Finish (index)

    // Forward pass equivalent: In this context, the user/Gantt already provides explicit start and end dates.
    // We treat the explicitly set start and end dates as Early Start (ES) and Early Finish (EF).
    leafTasks.forEach(t => {
        const tStart = t.start;
        const tEnd = t.end || t.start;
        if (tStart && tEnd) {
            es.set(t.id, getWorkingDayIdx(tStart));
            ef.set(t.id, getWorkingDayIdx(tEnd));
        }
    });

    // Build successors map for the Backward Pass
    const originalIdToTaskIdMap = new Map<string, string>();
    flatTasks.forEach(t => {
        if (t.id_original_project !== undefined && t.id_original_project !== null && !t.isWeeklySubtask) {
            originalIdToTaskIdMap.set(String(t.id_original_project), t.id);
        }
    });

    const successors = new Map<string, { id: string, type: string }[]>();
    leafTasks.forEach(t => {
        if (t.predecessors) {
            t.predecessors.forEach(predStr => {
                const match = predStr.match(/^([\d.]+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                if (match) {
                    const predOriginalId = String(match[1]);
                    let type = (match[2] || "FS").toUpperCase();
                    
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    
                    const predId = originalIdToTaskIdMap.get(predOriginalId);
                    if (predId) {
                        if (!successors.has(predId)) successors.set(predId, []);
                        successors.get(predId)!.push({ id: t.id, type });
                    }
                }
            });
        }
    });

    // Find the max end date (project's maximum Early Finish index)
    let maxEF = 0;
    ef.forEach((val) => {
        if (val > maxEF) maxEF = val;
    });

    // Backward pass: Initialize LF and LS for all leaf tasks to maxEF as a default starting ceiling
    leafTasks.forEach(t => {
        if (t.id && ef.has(t.id) && es.has(t.id)) {
            const taskEF = ef.get(t.id)!;
            const taskES = es.get(t.id)!;
            const duration = taskEF - taskES + 1;
            
            lf.set(t.id, maxEF);
            ls.set(t.id, maxEF - duration + 1);
        }
    });

    // Backward pass: Iterative relaxation for networks (handles dependency paths accurately)
    let changed = true;
    let iterations = 0;
    while(changed && iterations < 100) {
        changed = false;
        iterations++;
        leafTasks.forEach(t => {
            if (t.id && ef.has(t.id) && es.has(t.id)) {
                const succs = successors.get(t.id) || [];
                let minLF = maxEF; // Late Finish cannot exceed project end
                const taskEF = ef.get(t.id)!;
                const taskES = es.get(t.id)!;
                const duration = taskEF - taskES + 1;
                
                if (succs.length > 0) {
                    let calculatedMinLF = Number.MAX_SAFE_INTEGER;
                    succs.forEach(succ => {
                        const succLS = ls.get(succ.id);
                        const succLF = lf.get(succ.id);
                        
                        if (succLS !== undefined && succLF !== undefined) {
                            let possibleLF = Number.MAX_SAFE_INTEGER;
                            if (succ.type === "FS") {
                                possibleLF = succLS - 1; 
                            } else if (succ.type === "SS") {
                                possibleLF = succLS + duration - 1; 
                            } else if (succ.type === "FF") {
                                possibleLF = succLF; 
                            } else if (succ.type === "SF") {
                                possibleLF = succLF + duration - 1; 
                            }
                            if (possibleLF < calculatedMinLF) calculatedMinLF = possibleLF;
                        }
                    });
                    if (calculatedMinLF !== Number.MAX_SAFE_INTEGER) {
                        minLF = Math.min(minLF, calculatedMinLF);
                    }
                }
                
                const currentLF = lf.get(t.id);
                if (currentLF === undefined || minLF < currentLF) {
                    lf.set(t.id, minLF);
                    ls.set(t.id, minLF - duration + 1);
                    changed = true;
                }
            }
        });
    }

    // Determine Critical tasks based on Slack = LF - EF <= 0 (working day difference)
    const criticalTaskIds = new Set<string>();
    leafTasks.forEach(t => {
         if (t.id) {
             const tEF = ef.get(t.id);
             const tLF = lf.get(t.id);
             if (tEF !== undefined && tLF !== undefined) {
                 const slack = tLF - tEF;
                 if (slack <= 0) {
                     criticalTaskIds.add(t.id);
                 }
             }
         }
    });

    // Reconstruct tree and mark folders if any child is critical
    const applyCriticalRecursive = (list: Task[]): { tasks: Task[], hasCriticalChild: boolean } => {
        let anyChildCritical = false;
        const mappedList = list.map(t => {
            const hasOnlyWeeklySubtasks = t.children && t.children.length > 0 && t.children.every(c => c.isWeeklySubtask);
            
            if (t.type === 'task' && (!t.children || t.children.length === 0 || hasOnlyWeeklySubtasks)) {
                const isCrit = criticalTaskIds.has(t.id);
                if (isCrit) anyChildCritical = true;
                const childrenMapped = t.children ? t.children.map(c => ({ ...c, isCritical: false })) : [];
                return { ...t, isCritical: isCrit, children: childrenMapped };
            } else {
                const childRes = applyCriticalRecursive(t.children || []);
                if (childRes.hasCriticalChild) anyChildCritical = true;
                return { ...t, children: childRes.tasks, isCritical: childRes.hasCriticalChild };
            }
        });
        return { tasks: mappedList, hasCriticalChild: anyChildCritical };
    };

    return applyCriticalRecursive(tasksWithCalculatedFolders).tasks;
};

/**
 * Finds the parent task of a given task ID.
 */
export const findParentTask = (id: string | null, list: Task[]): Task | null => {
    if (!id || !list) return null;
    for (const t of list) {
        if (t.children && t.children.some(c => c.id === id)) {
            return t;
        }
        if (t.children) {
            const found = findParentTask(id, t.children);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Calculates the CPM slack (Late Finish - Early Finish) of all leaf tasks, anchoring the backward pass to contractualEndDate.
 */
export const calculateCriticalPathDelay = (
    tasks: Task[], 
    contractualEndDate: string,
    workingDays: boolean[] = [false, true, true, true, true, true, false], 
    holidays: { date: string; name: string }[] = []
): Map<string, number> => {
    // Helper to get local date string
    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    // Helper to check if a day is a working day
    const isWorkingDay = (date: Date): boolean => {
        const dayOfWeek = date.getDay();
        if (!workingDays[dayOfWeek]) return false;
        const dateStr = getLocalDateStr(date);
        return !holidays.some(h => h.date === dateStr);
    };

    const flatTasks: Task[] = [];
    const traverse = (t: Task[]) => t.forEach(task => {
        if (!task.isWeeklySubtask) {
            flatTasks.push(task);
            if (task.children) traverse(task.children);
        }
    });
    traverse(tasks);

    const slacks = new Map<string, number>();
    if (flatTasks.length === 0 || !contractualEndDate) return slacks;

    const leafTasks = flatTasks.filter(t => 
        !t.isWeeklySubtask && 
        t.type === 'task' && 
        (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))
    );
    if (leafTasks.length === 0) return slacks;

    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    leafTasks.forEach(t => {
        if (t.start) {
            const d = new Date(t.start + "T12:00:00");
            if (!minDate || d < minDate) minDate = d;
        }
        const tEnd = t.end || t.start;
        if (tEnd) {
            const d = new Date(tEnd + "T12:00:00");
            if (!maxDate || d > maxDate) maxDate = d;
        }
    });

    const targetDate = new Date(contractualEndDate + "T12:00:00");
    if (!maxDate || targetDate > maxDate) maxDate = targetDate;
    if (!minDate) return slacks;

    const startRange = new Date(minDate);
    startRange.setDate(startRange.getDate() - 60);
    const endRange = new Date(maxDate);
    endRange.setDate(endRange.getDate() + 60);

    const workingDaysList: string[] = [];
    const dateToIdx = new Map<string, number>();

    let curr = new Date(startRange);
    let idx = 0;
    while (curr <= endRange) {
        if (isWorkingDay(curr)) {
            const dateStr = getLocalDateStr(curr);
            workingDaysList.push(dateStr);
            dateToIdx.set(dateStr, idx);
            idx++;
        }
        curr.setDate(curr.getDate() + 1);
    }

    const getWorkingDayIdx = (dateStr: string): number => {
        if (dateToIdx.has(dateStr)) return dateToIdx.get(dateStr)!;
        const targetDate = new Date(dateStr + "T12:00:00");
        let bestIdx = 0;
        let minDiff = Number.MAX_SAFE_INTEGER;
        workingDaysList.forEach((wDateStr, wIdx) => {
            const wDate = new Date(wDateStr + "T12:00:00");
            const diff = Math.abs(wDate.getTime() - targetDate.getTime());
            if (diff < minDiff) {
                minDiff = diff;
                bestIdx = wIdx;
            }
        });
        return bestIdx;
    };

    const es = new Map<string, number>();
    const ef = new Map<string, number>();
    const ls = new Map<string, number>();
    const lf = new Map<string, number>();

    leafTasks.forEach(t => {
        const tStart = t.start;
        const tEnd = t.end || t.start;
        if (tStart && tEnd) {
            es.set(t.id, getWorkingDayIdx(tStart));
            ef.set(t.id, getWorkingDayIdx(tEnd));
        }
    });

    const originalIdToTaskIdMap = new Map<string, string>();
    flatTasks.forEach(t => {
        if (t.id_original_project !== undefined && t.id_original_project !== null && !t.isWeeklySubtask) {
            originalIdToTaskIdMap.set(String(t.id_original_project), t.id);
        }
    });

    const successors = new Map<string, { id: string, type: string }[]>();
    leafTasks.forEach(t => {
        if (t.predecessors) {
            t.predecessors.forEach(predStr => {
                const match = predStr.match(/^([\d.]+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                if (match) {
                    const predOriginalId = String(match[1]);
                    let type = (match[2] || "FS").toUpperCase();
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    const predId = originalIdToTaskIdMap.get(predOriginalId);
                    if (predId) {
                        if (!successors.has(predId)) successors.set(predId, []);
                        successors.get(predId)!.push({ id: t.id, type });
                    }
                }
            });
        }
    });

    const contractualEndIdx = getWorkingDayIdx(contractualEndDate);

    leafTasks.forEach(t => {
        if (t.id && ef.has(t.id) && es.has(t.id)) {
            const taskEF = ef.get(t.id)!;
            const taskES = es.get(t.id)!;
            const duration = taskEF - taskES + 1;
            lf.set(t.id, contractualEndIdx);
            ls.set(t.id, contractualEndIdx - duration + 1);
        }
    });

    let changed = true;
    let iterations = 0;
    while(changed && iterations < 100) {
        changed = false;
        iterations++;
        leafTasks.forEach(t => {
            if (t.id && ef.has(t.id) && es.has(t.id)) {
                const succs = successors.get(t.id) || [];
                let minLF = contractualEndIdx;
                const taskEF = ef.get(t.id)!;
                const taskES = es.get(t.id)!;
                const duration = taskEF - taskES + 1;
                
                if (succs.length > 0) {
                    let calculatedMinLF = Number.MAX_SAFE_INTEGER;
                    succs.forEach(succ => {
                        const succLS = ls.get(succ.id);
                        const succLF = lf.get(succ.id);
                        if (succLS !== undefined && succLF !== undefined) {
                            let possibleLF = Number.MAX_SAFE_INTEGER;
                            if (succ.type === "FS") {
                                possibleLF = succLS - 1;
                            } else if (succ.type === "SS") {
                                possibleLF = succLS + duration - 1;
                            } else if (succ.type === "FF") {
                                possibleLF = succLF;
                            } else if (succ.type === "SF") {
                                possibleLF = succLF + duration - 1;
                            }
                            if (possibleLF < calculatedMinLF) calculatedMinLF = possibleLF;
                        }
                    });
                    if (calculatedMinLF !== Number.MAX_SAFE_INTEGER) {
                        minLF = Math.min(minLF, calculatedMinLF);
                    }
                }
                
                const currentLF = lf.get(t.id);
                if (currentLF === undefined || minLF < currentLF) {
                    lf.set(t.id, minLF);
                    ls.set(t.id, minLF - duration + 1);
                    changed = true;
                }
            }
        });
    }

    leafTasks.forEach(t => {
        if (t.id) {
            const tEF = ef.get(t.id);
            const tLF = lf.get(t.id);
            if (tEF !== undefined && tLF !== undefined) {
                slacks.set(t.id, tLF - tEF);
            }
        }
    });

    return slacks;
};

export interface MultiSCurvePoint {
    name: string;
    date: string;
    weekIndex: number;
    planActive: number | null;
    real: number | null;
    [planKey: string]: any;
}

export const calculateMultiSCurve = (
    currentTasks: Task[],
    baselines: GanttBaseline[],
    progressHistory: ProjectProgressSnapshot[] = []
): MultiSCurvePoint[] => {
    // 1. Helper to extract leaf tasks (respecting the Gantt rule)
    const getLeafTasks = (list: Task[]): Task[] => {
        const flat: Task[] = [];
        const traverse = (tList: Task[]) => {
            tList.forEach(t => {
                if (t.isWeeklySubtask) return;
                const hasRealSubtasks = t.children && t.children.some(c => !c.isWeeklySubtask);
                if (t.type === 'task' && t.start && (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))) {
                    flat.push(t);
                } else if (t.children && hasRealSubtasks) {
                    traverse(t.children);
                }
            });
        };
        traverse(list);
        return flat;
    };

    // 2. Identify the active baseline (vigente)
    const activeBaseline = baselines.find(b => b.vigente);
    const activeTasks = activeBaseline ? activeBaseline.tasksSnapshot : currentTasks;

    // 3. Define all series to calculate
    const seriesToCalculate = [
        { key: 'planActive', tasks: activeTasks }
    ];

    baselines.forEach(b => {
        if (b.selectedForChart) {
            seriesToCalculate.push({
                key: `plan_rev${b.versionNumber}`,
                tasks: b.tasksSnapshot
            });
        }
    });

    // 4. Find global date range across all series
    let globalMinStart = Infinity;
    let globalMaxEnd = -Infinity;

    const seriesLeafTasks = seriesToCalculate.map(s => {
        const leafs = getLeafTasks(s.tasks);
        leafs.forEach(t => {
            if (t.start) {
                const startVal = new Date(t.start + "T12:00:00").getTime();
                if (startVal < globalMinStart) globalMinStart = startVal;
            }
            if (t.end || t.start) {
                const endVal = new Date((t.end || t.start)! + "T12:00:00").getTime();
                if (endVal > globalMaxEnd) globalMaxEnd = endVal;
            }
        });
        const totalDuration = leafs.reduce((sum, t) => sum + (t.duration || 0), 0);
        return {
            ...s,
            leafTasks: leafs,
            totalDuration
        };
    });

    // Fallback if no valid dates are found
    if (globalMinStart === Infinity) {
        globalMinStart = Date.now();
        globalMaxEnd = Date.now() + 30 * 86400000;
    }

    // 5. Generate weekly points
    const points: MultiSCurvePoint[] = [];
    let curr = new Date(globalMinStart);

    // Normalize to start of week (Monday)
    const day = curr.getDay();
    const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
    curr.setDate(diff);

    while (curr.getTime() <= globalMaxEnd || points.length < 4) {
        const dateStr = curr.toISOString().split('T')[0];
        const dateLabel = curr.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
        const compareTime = curr.getTime();

        const point: any = {
            name: dateLabel,
            date: dateStr,
            weekIndex: points.length + 1
        };

        // Calculate planned progress for each series
        seriesLeafTasks.forEach(s => {
            let plannedDurationAccum = 0;
            s.leafTasks.forEach(t => {
                const tStart = new Date(t.start! + "T12:00:00").getTime();
                const tEnd = new Date((t.end || t.start)! + "T12:00:00").getTime();
                const tDur = t.duration || 0;

                if (compareTime >= tEnd) {
                    plannedDurationAccum += tDur;
                } else if (compareTime > tStart) {
                    const totalDays = Math.max(86400000, tEnd - tStart);
                    const elapsedDays = compareTime - tStart;
                    plannedDurationAccum += (elapsedDays / totalDays) * tDur;
                }
            });

            const planPct = s.totalDuration > 0
                ? Number(truncateProgress((plannedDurationAccum / s.totalDuration) * 100, 2))
                : 0;

            point[s.key] = Math.min(100, planPct);
        });

        // Calculate real progress from history
        let realPct: number | null = null;
        if (progressHistory && Array.isArray(progressHistory) && progressHistory.length > 0) {
            const validHistory = progressHistory.filter(h => h && h.date);
            if (validHistory.length > 0) {
                const sortedHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));
                const lastRecord = sortedHistory[sortedHistory.length - 1];
                const lastRecordTime = new Date(lastRecord.date + "T23:59:59").getTime();

                if (compareTime <= lastRecordTime) {
                    const endOfCurrentWeekStr = new Date(compareTime + (7 * 86400000)).toISOString().split('T')[0];
                    const pastOrCurrentRecords = sortedHistory.filter(r => r.date <= endOfCurrentWeekStr);

                    if (pastOrCurrentRecords.length > 0) {
                        realPct = pastOrCurrentRecords[pastOrCurrentRecords.length - 1].progress;
                    } else {
                        realPct = 0;
                    }
                }
            }
        }
        point.real = realPct !== null ? Math.min(100, realPct) : null;

        points.push(point);

        curr.setDate(curr.getDate() + 7);
        if (points.length > 100) break; // Hard limit for safety
    }

    return points;
};
